import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import { CampaignDatePresets, CampaignFilterParam } from '@retail/campaign/types';
import { CampaignTypeOption } from '@retail/campaign/components';

export const searchParamKeys: Record<CampaignFilterParam, CampaignFilterParam> = {
  date_preset: 'date_preset',
  search: 'search',
  campaign_type: 'campaign_type',
  mandatory: 'mandatory',
  brands: 'brands',
  start_date: 'start_date',
  end_date: 'end_date',
} as const;

function parseBool(v: string | null): boolean | null {
  return v === null ? null : v === 'true';
}

function parseDate(v: string | null): Date | null {
  if (!v) return null;
  const d = dayjs(v, 'YYYY-MM-DD', true);
  return d.isValid() ? d.toDate() : null;
}

function formatDate(d: Date | null): string | null {
  return d ? dayjs(d).format('YYYY-MM-DD') : null;
}

function splitCsv(v: string | null): string[] {
  if (!v) return [];
  return v.split(',').filter(Boolean);
}

function joinCsv(values: string[]): string | null {
  return values.length ? values.join(',') : null;
}

export function useCampaignsFilters() {
  const [params, setParams] = useSearchParams();

  const datePreset = CampaignDatePresets.parse(params.get(searchParamKeys.date_preset));
  const searchQuery = params.get(searchParamKeys.search) ?? '';
  const typeParam = (params.get(searchParamKeys.campaign_type) as CampaignTypeOption) ?? 'ALL';
  const mandatoryParam = parseBool(params.get(searchParamKeys.mandatory));
  const brandIds = splitCsv(params.get(searchParamKeys.brands));
  const fromDate = parseDate(params.get(searchParamKeys.start_date));
  const toDate = parseDate(params.get(searchParamKeys.end_date));

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams((prev) => {
        if (value && value.length > 0) prev.set(key, value);
        else prev.delete(key);
        return prev;
      });
    },
    [setParams]
  );

  const setDatePreset = useCallback(
    (datePreset: string) => setParam(searchParamKeys.date_preset, datePreset),
    [setParam]
  );

  const setSearchQuery = useCallback(
    (q: string) => setParam(searchParamKeys.search, q || null),
    [setParam]
  );
  const setCampaignType = useCallback(
    (type: CampaignTypeOption) =>
      setParam(searchParamKeys.campaign_type, type === 'ALL' ? null : type),
    [setParam]
  );
  const setIsMandatory = useCallback(
    (b: boolean | null) => {
      const value = b === null ? null : b ? 'true' : 'false';
      setParam(searchParamKeys.mandatory, value);
    },
    [setParam]
  );
  const setBrandIds = useCallback(
    (ids: string[]) => setParam(searchParamKeys.brands, joinCsv(ids)),
    [setParam]
  );
  const setFromDate = useCallback(
    (d: Date | null) => setParam(searchParamKeys.start_date, formatDate(d)),
    [setParam]
  );
  const setToDate = useCallback(
    (d: Date | null) => setParam(searchParamKeys.end_date, formatDate(d)),
    [setParam]
  );

  const resetFilters = useCallback(() => {
    setParams((prev) => {
      Object.values(searchParamKeys).forEach((k) => prev.delete(k));
      return prev;
    });
  }, [setParams]);

  return useMemo(
    () => ({
      datePreset,
      setDatePreset,
      searchQuery,
      setSearchQuery,
      campaignType: typeParam,
      setCampaignType,
      isMandatory: mandatoryParam,
      setIsMandatory,
      brandIds,
      setBrandIds,
      fromDate,
      setFromDate,
      toDate,
      setToDate,
      resetFilters,
    }),
    [
      brandIds,
      datePreset,
      fromDate,
      mandatoryParam,
      resetFilters,
      searchQuery,
      setBrandIds,
      setCampaignType,
      setDatePreset,
      setFromDate,
      setIsMandatory,
      setSearchQuery,
      setToDate,
      toDate,
      typeParam,
    ]
  );
}
